import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  type OnChanges,
  type OnDestroy,
  type OnInit,
  type SimpleChanges,
  inject,
  input,
} from '@angular/core';
import { ChangeDetectorRef, Component, output } from '@angular/core';

import { BehaviorSubject, Subject, take } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { User } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import type { Visibility, VisibilityLevel } from '@core/visibility-types';

import type { VisibilitySelectLabels } from './types';
import type { VisibilityInfoContext } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import { VisibilitySelectorAnalytics } from './visibility-selector.analytics';
import { VisibilitySelectorComponent } from './visibility-selector.component';
import { VisibilitySelectorService } from './visibility-selector.service';
import type { RoleWithAdmins } from '../../types';

@Component({
  selector: 'rt-visibility-selector-container',
  templateUrl: 'visibility-selector-container.component.html',
  styleUrls: ['./visibility-selector-container.component.less'],
  providers: [VisibilitySelectorAnalytics, VisibilitySelectorService],
  imports: [VisibilitySelectorComponent],
})
export class VisibilitySelectorContainerComponent implements OnChanges, OnInit, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(VisibilitySelectorAnalytics);
  private readonly visibilitySelectorService = inject(VisibilitySelectorService);

  public readonly roleSelectionRequired = input<boolean>(true);
  public readonly hasOnlyMeLevel = input<boolean>(true);
  public readonly hasSelectTeamMembers = input<boolean>(true);
  public readonly hasLabel = input<boolean>(true);
  public readonly hasLockIcon = input<boolean>(true);
  public readonly editable = input<boolean>(true);
  public readonly wrapOnSmallerResolutions = input<boolean>(false);
  public readonly visibility = input<Visibility>(undefined!);
  public readonly avatarMaxLength = input<number>(3);
  public readonly infoContext = input<VisibilityInfoContext>('view');
  public readonly buttonVariant = input<'plain' | 'outline'>('plain');
  public readonly protectedUsersPosition = input<'outside' | 'inside'>('outside');
  public readonly labels = input<VisibilitySelectLabels>(undefined!);
  public readonly avatarSize = input<'small' | 'small-x'>('small');
  public readonly candidateId = input<number>();
  public readonly offerId = input<number>();
  public readonly groupUsersByAccess = input<boolean | null>();
  public readonly disabledUserCopy = input<string>();

  public readonly dropdownPositions = input<ConnectionPositionPair[]>([
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ]);
  public readonly dropdownOffsetX = input<number>(0);
  public readonly dropdownOffsetY = input<number>(0);

  public visibilityChanges = output<Visibility>();

  public allowAdminsChange: boolean = true;
  public admins: User[] = [];
  public rolesWithAdmins: RoleWithAdmins[] = [];

  private visibility$ = new BehaviorSubject<Visibility | null>(null);
  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.visibility) {
      this.visibility$.next(this.visibility());
    }
  }

  public ngOnInit(): void {
    this.visibilitySelectorService.featureVisibility$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(isEnabled => {
        this.allowAdminsChange = isEnabled;
        this.cdRef.detectChanges();
      });

    this.visibilitySelectorService
      .getVisibilityUsersData(this.visibility$.pipe(filterBoolean))
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(usersData => {
        this.admins = usersData.admins;
        this.rolesWithAdmins = usersData.rolesWithAdmins;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onVisibilityChanges(level: VisibilityLevel): void {
    this.visibilitySelectorService
      .onVisibilityChanges(level, this.visibility(), {
        roleSelectionRequired: this.roleSelectionRequired(),
        candidateId: this.candidateId(),
        offerId: this.offerId(),
        groupUsersByAccess: this.groupUsersByAccess(),
        disabledUserCopy: this.disabledUserCopy(),
      })
      .pipe(take(1))
      .subscribe(visibility => {
        if (!visibility) return;

        this.visibilityChanges.emit(visibility);
        this.cdRef.detectChanges();
      });
  }

  public onVisibilityDropdownToggle(open: boolean): void {
    if (open) {
      this.analytics.trackDropdownOpen();
    }
  }
}
